/**
 * Button — actions across the Mini Apps.
 *
 * ``primary`` is the gold, glowing call-to-action (used sparingly — the page's
 * signature). ``secondary`` is a quiet outlined action; ``ghost`` is text-only;
 * ``danger`` is for destructive/cancel actions.
 */
import type { ButtonHTMLAttributes, ReactNode } from "react";

import { cn } from "../utils/cn";
import styles from "./Button.module.css";

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: "primary" | "secondary" | "ghost" | "danger";
  readonly size?: "sm" | "md" | "lg";
  /** Stretch to the container width. Defaults to ``false``. */
  readonly fullWidth?: boolean;
  /** Show a spinner and block interaction. Defaults to ``false``. */
  readonly loading?: boolean;
  /** Leading icon/element. */
  readonly leading?: ReactNode;
  readonly children?: ReactNode;
}

export function Button({
  variant = "primary",
  size = "md",
  fullWidth = false,
  loading = false,
  leading,
  className,
  children,
  disabled,
  ...rest
}: ButtonProps): JSX.Element {
  return (
    <button
      type="button"
      className={cn(
        styles.button,
        styles[variant],
        styles[size],
        fullWidth && styles.fullWidth,
        loading && styles.loading,
        className,
      )}
      disabled={disabled === true || loading}
      aria-busy={loading}
      {...rest}
    >
      {loading ? <span className={styles.spinner} aria-hidden="true" /> : null}
      {!loading && leading ? (
        <span className={styles.leading} aria-hidden="true">
          {leading}
        </span>
      ) : null}
      <span className={styles.label}>{children}</span>
    </button>
  );
}
